// Dana's contextual recognition suggestions for a work item. Fetches OFFERED
// candidates (scoped to the caller — only suggestions the caller may act on) and
// renders each as a pre-filled RecognizeInline card. Dana SUGGESTS; nothing is
// created until the human confirms. Silent when there are no suggestions.
import React, { useEffect, useState } from 'react';
import { RecognizeInline } from '@/features/shared/components/RecognizeInline';
import { useEntitlements } from '@/features/entitlements/useEntitlements';
import {
  getRecognitionCandidates, type RecognitionCandidate,
} from '@/services/recognitionService';

interface Props {
  workItemId: string;
  /** Resolve a subject's display name (built from the work item's discussion). */
  nameByUserId?: Record<string, string>;
  /** Bump to re-open the polling window — e.g. the discussion's comment count.
   *  A Dana suggestion lands a few seconds AFTER a comment, so posting one should
   *  make the card appear without a manual reload. */
  refreshKey?: number | string;
}

export const RecognitionSuggestions: React.FC<Props> = ({ workItemId, nameByUserId, refreshKey }) => {
  const { hasProduct } = useEntitlements(); // fails open for un-provisioned tenants
  const entitled = hasProduct('performance');
  const [cands, setCands] = useState<RecognitionCandidate[]>([]);
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  // Detection is async (comment → event → Dana classify → candidate, ~10s), and
  // the component would otherwise only fetch once on mount. So poll a short bounded
  // window and stop as soon as a candidate arrives (or the window closes). The
  // window re-opens whenever refreshKey changes (a new comment was posted).
  useEffect(() => {
    if (!entitled) return; // don't even fetch when the product is off
    let alive = true;
    let attempts = 0;
    const MAX_ATTEMPTS = 8; // ~48s at 6s cadence
    let timer: ReturnType<typeof setInterval> | undefined;
    const poll = () => {
      attempts += 1;
      getRecognitionCandidates(workItemId)
        .then((c) => {
          if (!alive) return;
          setCands(c);
          if (c.length > 0 || attempts >= MAX_ATTEMPTS) {
            if (timer) clearInterval(timer);
          }
        })
        .catch(() => { /* best-effort; suggestions are additive */ });
    };
    poll(); // immediate
    timer = setInterval(poll, 6000);
    return () => { alive = false; if (timer) clearInterval(timer); };
  }, [workItemId, entitled, refreshKey]);

  const visible = cands.filter((c) => !dismissed.has(c.id) && c.subject_user_id);
  if (visible.length === 0) return null;

  const drop = (id: string) => setDismissed((s) => new Set(s).add(id));

  return (
    <div className="space-y-2">
      {visible.map((c) => (
        <RecognizeInline
          key={c.id}
          recipientUserId={c.subject_user_id as string}
          recipientName={nameByUserId?.[c.subject_user_id as string]}
          sourceWorkItemId={workItemId}
          suggestion={{
            candidateId: c.id,
            category: c.suggested_category,
            title: c.suggested_title,
            message: c.suggested_message,
            reason: c.reason_summary,
          }}
          onDone={() => drop(c.id)}
        />
      ))}
    </div>
  );
};

export default RecognitionSuggestions;
